import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProjectContext } from "../contexts/ProjectContextProvider";
import { OrganizationContext } from "../contexts/OrganizationContextProvider";
import { GoBell } from "react-icons/go";
import { MdOutlineHub } from "react-icons/md";
import { IoSettingsOutline } from "react-icons/io5";
import CustomSkeleton from "./CustomSkeleton";
import { RAGStatus } from "./Status";
import { FiAlertOctagon } from "react-icons/fi";
import styles from "./SideMenu.module.css";
import { RiBox3Fill } from "react-icons/ri";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";
import { GrUserSettings } from "react-icons/gr";
import { IoIosArrowDown } from "react-icons/io";
import { RiOrganizationChart } from "react-icons/ri";
import { FiAlertTriangle } from "react-icons/fi";
import { TbBulb } from "react-icons/tb";
import { MdContentPasteSearch } from "react-icons/md";
import NoticeTag from "./ui/tags/NoticeTag";
import { useNotification } from "../contexts/NotificationContextProvider";

function SideMenu({ toggleDrawer }) {
  const navigate = useNavigate();
  const { unreadNotificationCount } = useNotification();
  const [active, setActive] = useState("all-projects");
  
  // Notification title with unread count badge
const NotificationTitle = (
  <span className="d-flex align-items-center gap-2">
    <span>Notifications</span>
    {unreadNotificationCount>0 && <span className="badge bg-danger rounded-5">{unreadNotificationCount}</span>}
  </span>
);


  const handleMenuTitleClick = (title, uri, open) => {
    setActive(title);
    navigate(uri);
    !open && toggleDrawer && toggleDrawer(false); // Close the drawer after navigation
  };

  return (
    <section className="p-4">
      <div>
        <div className="mb-2">
          <OrganizationMenu />
        </div>

        <ProjectMenu
          active={active}
          handleMenuTitleClick={handleMenuTitleClick}
        />

        <MenuTitle
          title={
            <span
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: "10px",
                whiteSpace: "nowrap",
              }}
            >
              <span>Research SpotLight</span>
              <NoticeTag title="New" variant="blue" small />
            </span>
          }
          active={active === "research-spotlight"}
          icon={<MdContentPasteSearch size={24} />}
          onClick={() =>
            handleMenuTitleClick("research-spotlight", "/research-spotlight")
          }
          className=""
        />

        <LearningHubMenu
          active={active}
          handleMenuTitleClick={handleMenuTitleClick}
        />

        <MenuTitle
          title={NotificationTitle}
          active={active === "notification"}
          icon={<GoBell size={22} />}
          onClick={() =>
            handleMenuTitleClick("notification", "/user/notification")
          }
        />

        {/* temporary hidden */}
        {/* <MenuTitle
          title="Alerts"
          active={active === "alert"}
          onClick={() => handleMenuTitleClick("alert", "/alert")}
          icon={<FiAlertOctagon size={24} />}
        /> */}
        <hr className="pb-0 mb-2 mt-2 " style={{ color: "#737070" }} />
        <MenuTitle
          title="Organization"
          active={active === "organization"}
          icon={<IoSettingsOutline size={22} />}
          onClick={() => handleMenuTitleClick("organization", "/organization")}
        />
      </div>
    </section>
  );
}

export default SideMenu;

const OrganizationMenu = () => {
  const { data: organization } = useContext(OrganizationContext);

  return (
    <>
      <div
        className={` rounded-4 d-flex justify-content-between align-items-center p-3 `}
        style={{
          background:
            "linear-gradient(to right, rgba(11, 167, 255, 0.03), rgba(0, 150, 235, 0.03))",
        }}
      >
        <h6
          className={` d-flex align-items-center gap-2  mb-0 text-nowrap text-truncate text-capitalize`}
        >
          <span
            style={{
              backgroundColor: "rgb(236, 236, 236,0.8)",
              borderRadius: "50%",
              padding: "8px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RiOrganizationChart
              size={24}
              className={`${styles.menuTitleActive}`}
            />{" "}
          </span>
          <span style={{ color: "var(--gray)" }}>
            {organization?.current_organization?.name}
          </span>
        </h6>
        <div></div>
      </div>
    </>
  );
};

const ProjectMenu = ({ active, handleMenuTitleClick }) => {
  const [showProjects, setShowProjects] = React.useState(true);
  const { data: projectArray, error, loading } = useContext(ProjectContext);

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <>
      <MenuTitle
        active={active === "all-projects"}
        collapsible
        title="All Projects"
        icon={<RiBox3Fill size={24} />}
        onClick={() => handleMenuTitleClick("all-projects", "/home")}
        onClickDownArrow={() => setShowProjects(!showProjects)}
      />
      {!loading &&
        showProjects &&
        projectArray?.map((project) => (
          <div
            onClick={() =>
              handleMenuTitleClick("all-projects", `project/${project?.id}`)
            }
            className="ps-3 ps-lg-4  mt-2"
            style={{ cursor: "pointer" }}
            key={project?.id} // Use project.id as the key for better uniqueness
          >
            <div className="d-flex justify-content-between align-items-center ps-3 pe-4 py-1 ">
              <h6 className={`p3 text-nowrap text-truncate text-capitalize`}>
                <HiOutlineSquare3Stack3D
                  size={17}
                  className={`${styles.menuTitleActive}`}
                />
                <span
                  className={`${styles.menuTitleInactiveTitle} ms-2 fw-semibold p3`}
                  title={project?.name}
                >
                  {project?.name}
                </span>
              </h6>
              <div className="d-flex align-items-center position-relative">
                {project?.role === "admin" && (
                  <GrUserSettings size={16} color="gray" className="me-3" />
                )}
                <RAGStatus deadline={project?.end_date} />
              </div>
            </div>
          </div>
        ))}
      {loading && showProjects && (
        <CustomSkeleton className="ms-4 py-1 my-2" count={5} />
      )}

      {/* no projects in the list */}
      {projectArray && showProjects && projectArray.length === 0 && (
        <div className="ps-3 ps-lg-4 mt-2">
          <h6 className="p6 fw-semibold text-secondary lh-base">
            No projects found. To create a new project, click the "+New Project"
            button on the top right corner of the page.
          </h6>
        </div>
      )}
    </>
  );
};

// New component for Learning Hub with sub-items(risk and learning)
const LearningHubMenu = ({ active, handleMenuTitleClick }) => {
  const [showSubItems, setShowSubItems] = React.useState(false);

  return (
    <div className="learning-hub-menu">
      <MenuTitle
        active={active === "learning-hub"}
        collapsible
        onClickDownArrow={() => setShowSubItems((prev) => !prev)}
        title="Learning Hub"
        icon={<MdOutlineHub size={24} />}
        onClick={() => {
          if (!showSubItems) setShowSubItems(true);
          handleMenuTitleClick("learning-hub", "/learning-hub", true);
        }}
      />

      {showSubItems && (
        <ul className="sub-menu ps-4 gap-1 mb-0">
          <li
            className="d-flex justify-content-start align-items-center px-3 py-1 "
            style={{ cursor: "pointer" }}
            onClick={() => handleMenuTitleClick("learning-hub", "/risk")}
          >
            <h6
              className={`p3 text-nowrap text-truncate d-flex align-items-center `}
            >
              <span>
                <FiAlertTriangle
                  size={19}
                  className={`${styles.menuTitleActive}`}
                />
              </span>
              <span
                className={`${styles.menuTitleInactiveTitle} ms-2 fw-semibold`}
              >
                Risks
              </span>{" "}
              {/* Added margin for better spacing */}
            </h6>
          </li>
          <li
            className="d-flex justify-content-start align-items-center px-3 py-1 "
            style={{ cursor: "pointer" }}
            onClick={() => handleMenuTitleClick("learning-hub", "/lesson")}
          >
            <h6
              className={`p3 text-nowrap text-truncate d-flex align-items-center`}
            >
              <TbBulb size={23} className={`${styles.menuTitleActive} `} />
              <span
                className={`${styles.menuTitleInactiveTitle} ms-1 fw-semibold`}
              >
                Lessons
              </span>{" "}
              {/* Added margin for better spacing */}
            </h6>
          </li>
        </ul>
      )}
    </div>
  );
};

// this is main headings in the side menu
const MenuTitle = ({
  title,
  icon,
  onClick,
  style,
  className,
  active,
  collapsible = false,
  onClickDownArrow,
}) => {
  return (
    <div
      onClick={onClick}
      style={{ cursor: "pointer", ...style }}
      className={`${
        active ? styles.menuActive : styles.menuTitleInactive
      } rounded-4 d-flex justify-content-between align-items-center p-3  ${className}`}
    >
      <h6
        className={`${
          active ? styles.menuTitleActive : ""
        } d-flex align-items-center gap-2 fw-semibold mb-0`}
      >
        <span
          className={`${
            active ? styles.menuTitleActive : styles.menuTitleInactiveIcon
          }`}
        >
          {icon}{" "}
        </span>
        <span
          className={`${
            active ? styles.menuTitleActive : styles.menuTitleInactiveTitle
          } fw-semibold`}
        >
          {title}
        </span>
      </h6>
      <div>
        {collapsible && (
          <IoIosArrowDown
            size={20}
            onClick={onClickDownArrow}
            style={{ cursor: "pointer" }}
          />
        )}
      </div>
    </div>
  );
};

